import { AnimatePresence, motion } from "framer-motion";
import { Search, UserPlus, X } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import "../App.css"; // If it uses general styles, or create specific CSS
import { usePatientDashboard } from "../hooks/usePatientDashboard";
import Button from "./ui/Button";
import Input from "./ui/Input";
import Select from "./ui/Select";
import Textarea from "./ui/Textarea";
import BaseModal from "./ui/BaseModal";

const PatientDashboard = ({
  patients,
  fetchPatients,
  onSelectPatient,
  onDeletePatient,
  selectedPatient,
  showPatientSelector,
  onShowPatientSelector,
}) => {
  const {
    newPatient,
    isLoading,
    error,
    showForm,
    setShowForm,
    searchQuery,
    handleSearchChange,
    displayedPatients,
    hasMore,
    loadMore,
    handleInputChange,
    handleSubmit,
  } = usePatientDashboard(fetchPatients, onSelectPatient, patients);

  const [showModal, setShowModal] = useState(false);
  const [patientIdToDelete, setPatientIdToDelete] = useState(null);

  // Set up intersection observer for infinite scroll
  const observer = useRef();
  const lastPatientElementRef = useCallback(
    (node) => {
      if (isLoading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          loadMore();
        }
      });
      if (node) observer.current.observe(node);
    },
    [isLoading, hasMore, loadMore]
  );

  // Compact view for when patient is selected
  if (selectedPatient && !showPatientSelector) {
    return (
      <div className="space-y-4 md:space-y-6">
        <motion.div
          className="bg-white rounded-xl p-3 md:p-4 border border-blue-200 shadow-sm relative"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <div className="space-y-3">
            {/* Header with badge and title */}
            <h3 className="text-xl font-semibold text-[#1c3f70] truncate">
              Selected Patient
            </h3>

            {/* Patient info and change button */}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center">
                <span className="w-2.5 h-2.5 bg-[#2a5caa] rounded-full animate-pulse mr-2"></span>
                <span className="text-base text-[#1c3f70] font-medium truncate max-w-[150px] sm:max-w-full">
                  {selectedPatient.name}
                </span>
                <span className="ml-2 text-sm text-gray-500 whitespace-nowrap">
                  {selectedPatient.age} years, {selectedPatient.gender}
                </span>
              </div>

              <Button
                onClick={() => onShowPatientSelector(true)}
                className="px-3 py-1.5 bg-gradient-to-r from-[#2a5caa] to-[#3963aa] text-white rounded-lg text-sm font-medium shadow-sm hover:shadow-md transition-all whitespace-nowrap flex-shrink-0"
              >
                Change Patient
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6 h-[100%] flex flex-col">
      <div className="flex items-center justify-between gap-2 group">
        <h3 className="text-base md:text-lg font-semibold center text-[#1c3f70]">
          {selectedPatient && showPatientSelector
            ? "Select Patient"
            : "Patient Records"}
        </h3>

        {selectedPatient && showPatientSelector ? (
          <>
            <Button
              onClick={() => setShowForm(!showForm)}
              className={`px-3 py-1 md:px-4 md:py-1.5 rounded-lg text-white text-xs md:text-sm font-medium shadow-sm transition-all  ${
                showForm
                  ? "bg-gray-600 hover:bg-gray-700"
                  : "bg-gradient-to-r from-[#2a5caa] to-[#3963aa] hover:shadow-md"
              }`}
            >
              {showForm ? "Cancel" : "Add Patient"}
            </Button>
            <Button
              onClick={() => onShowPatientSelector(false)}
              className=" bg-gradient-to-r from-[#2a5caa] to-[#3963aa] text-white px-3 py-1 md:px-4 md:py-1.5 rounded-lg text-xs md:text-sm font-medium shadow-sm transition-all"
            >
              <X />
            </Button>
          </>
        ) : (
          <Button
            onClick={() => setShowForm(!showForm)}
            className={`px-3 py-1 md:px-4 md:py-1.5 rounded-lg text-white text-xs md:text-sm font-medium shadow-sm transition-all  ${
              showForm
                ? "bg-gray-600 hover:bg-gray-700"
                : "bg-gradient-to-r from-[#2a5caa] to-[#3963aa] hover:shadow-md"
            }`}
          >
            {showForm ? "Cancel" : "Add Patient"}
          </Button>
        )}
      </div>

      <AnimatePresence>
        {showForm &&
          (!selectedPatient || (selectedPatient && showPatientSelector)) && (
            <motion.div
              className="bg-white rounded-xl p-3 md:p-5 border border-gray-100 shadow-md"
              initial={{ opacity: 0, height: 0, y: -10 }}
              animate={{ opacity: 1, height: "auto", y: 0 }}
              exit={{ opacity: 0, height: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <form onSubmit={handleSubmit} className="space-y-3 md:space-y-4">
                <Input
                  label="Name"
                  type="text"
                  onChange={handleInputChange}
                  name="name"
                  value={newPatient.name}
                  required
                  className="mt-3 w-full"
                />
                <Input
                  label="Age"
                  type="number"
                  onChange={handleInputChange}
                  name="age"
                  value={newPatient.age}
                  required
                  className="mt-3 w-full"
                />
                <Select
                  label="Gender"
                  onChange={handleInputChange}
                  name="gender"
                  defaultValue={newPatient.gender}
                  options={[
                    { value: "Female", label: "Female" },
                    { value: "Male", label: "Male" },
                    { value: "Other", label: "Other" },
                  ]}
                  className="mt-3"
                />
                <Textarea
                  label="Initial Diagnosis/Notes:"
                  onChange={handleInputChange}
                  name="diagnosis"
                  value={newPatient.diagnosis}
                  rows={2}
                  className="text-sm"
                />
                <div className="flex space-x-2 pt-1 md:pt-2">
                  <Button
                    type="submit"
                    disabled={isLoading}
                    className={`px-3 py-1.5 md:px-5 md:py-2 bg-gradient-to-r from-[#2a5caa] to-[#3963aa] text-white rounded-lg text-xs md:text-sm font-medium shadow-sm hover:shadow-md transition-all ${
                      isLoading ? "opacity-70 cursor-not-allowed" : ""
                    }`}
                  >
                    {isLoading ? "Creating..." : "Create Patient"}
                  </Button>
                </div>
                {error && (
                  <motion.div
                    className="p-2 md:p-3 bg-red-50 border border-red-200 rounded-lg"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    <p className="text-red-600 text-xs md:text-sm">{error}</p>
                  </motion.div>
                )}
              </form>
            </motion.div>
          )}
      </AnimatePresence>

      {/* Search input */}
      <div className="relative">
        <Input
          type="text"
          placeholder="Search patients..."
          value={searchQuery}
          onChange={handleSearchChange}
          className="pl-10 text-sm w-full"
        />
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-5 w-5 text-gray-400" />
        </div>
      </div>

      <div className="flex flex-col overflow-hidden">
        {displayedPatients.length === 0 ? (
          <motion.div
            className="text-center p-4 md:p-8 bg-gray-50 rounded-xl border border-dashed border-gray-300"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {patients.length === 0 ? (
              <>
                <UserPlus
                  className="h-12 w-12 md:h-16 md:w-16 mx-auto text-gray-400 mb-3 md:mb-4"
                  strokeWidth={1.5}
                />
                <p className="text-gray-600 text-sm md:text-base">
                  No patients found. Add a patient to get started.
                </p>
              </>
            ) : (
              <>
                <Search
                  className="h-12 w-12 md:h-16 md:w-16 mx-auto text-gray-400 mb-3 md:mb-4"
                  strokeWidth={1.5}
                />
                <p className="text-gray-600 text-sm md:text-base">
                  No patients match your search. Try different keywords.
                </p>
              </>
            )}
          </motion.div>
        ) : (
          <motion.ul className="space-y-2 md:space-y-3 overflow-y-auto pr-1 md:pr-2 pb-1 md:pb-2">
            {displayedPatients.map((patient, index) => {
              const isLastElement = index === displayedPatients.length - 1;
              return (
                <motion.li
                  key={patient.id}
                  ref={isLastElement ? lastPatientElementRef : null}
                  className={`p-3 md:p-4 rounded-xl border transition-all duration-300 cursor-pointer group ${
                    selectedPatient?.id === patient.id
                      ? "bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200 shadow"
                      : "bg-white border-gray-100 hover:shadow-md"
                  }`}
                  onClick={() => {
                    onSelectPatient(patient);
                    if (selectedPatient) {
                      onShowPatientSelector(false);
                    }
                  }}
                  whileTap={{ scale: 0.99 }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05, duration: 0.3 }}
                >
                  <div className="flex justify-between">
                    <div>
                      <h4 className="font-semibold text-[#1c3f70] text-sm md:text-base">
                        {patient.name}
                      </h4>
                      <p className="text-xs md:text-sm text-gray-600">
                        {patient.age} years, {patient.gender}
                      </p>
                    </div>
                    <div className="flex items-center">
                      {selectedPatient?.id === patient.id ? (
                        <span className="w-2 h-2 md:w-2.5 md:h-2.5 bg-[#2a5caa] rounded-full animate-pulse"></span>
                      ) : (
                        <>
                          <Button
                            variant="danger"
                            onClick={(e) => {
                              e.stopPropagation();
                              setPatientIdToDelete(patient.id);
                              setShowModal(true);
                            }}
                            className="ml-2 px-2 py-1 text-xs bg-red-100 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            Delete
                          </Button>
                          <Button
                            variant="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              onSelectPatient(patient);
                              if (selectedPatient) {
                                onShowPatientSelector(false);
                              }
                            }}
                            className="ml-2 px-2 py-1 text-xs bg-blue-100 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            Select
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  {patient.diagnosis && (
                    <div className="mt-1.5 md:mt-2 p-2 md:p-2.5 bg-gray-50 rounded-lg text-xs md:text-sm text-gray-700">
                      {patient.diagnosis}
                    </div>
                  )}
                  <div className="mt-1 md:mt-1.5 text-xs text-gray-400">
                    ID: {patient.id}
                  </div>
                </motion.li>
              );
            })}
            {isLoading && (
              <motion.div
                className="flex justify-center p-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <div className="w-5 h-5 rounded-full bg-[#2a5caa] animate-pulse"></div>
              </motion.div>
            )}
          </motion.ul>
        )}
      </div>
      <BaseModal
        open={showModal}
        title="Confirm Delete Patient"
        description="Are you sure you want to delete this patient? This action cannot be undone."
        onClose={() => setShowModal(false)}
        onConfirm={() => {
          onDeletePatient(patientIdToDelete);
          setShowModal(false);
        }}
      >
        ID: {patientIdToDelete}
      </BaseModal>
    </div>
  );
};

export default PatientDashboard;
